import type { FileForProcessing } from '../Sample/ISample.ts'
import type { IPipelineInformation } from '../Sample/IPipelineInformation.ts'
import type { OrderAlert } from './orderAlerts.ts'

/** Share of a sample's or order's reads removed by quality filtering above which the filter itself is suspect. */
export const QUALITY_FILTER_LOSS_WARN_PERCENT = 90

export interface QualityFilterLoss {
  /** Order-wide, over samples, one decimal. */
  lostPercent: number
  severeSamples: number
  samples: number
  /** "283–700 bp", or null when the samples do not share one window. */
  lengthWindow: string | null
}

const readsBefore = (pi: IPipelineInformation): number => pi.sequenceCountBeforeQualityFiltered ?? pi.sequenceCountMerged ?? 0

const lostPercent = (before: number, after: number): number => (100 * (before - after)) / before

function describeWindow(min?: number, max?: number): string | null {
  if (min && max) return `${min}–${max} bp`
  if (min) return `at least ${min} bp`
  if (max) return `at most ${max} bp`
  return null
}

function sharedLengthWindow(items: readonly FileForProcessing[]): string | null {
  const windows = new Set(items.map((i) => describeWindow(i.minBasePairLength, i.maxBasePairLength)))
  return windows.size === 1 ? [...windows][0] : null
}

/**
 * The quality-filter loss of [items] when it is severe: more than [QUALITY_FILTER_LOSS_WARN_PERCENT] of the order's
 * reads, or of most samples' reads. Null otherwise. Negative controls are left out: their few reads make any share
 * meaningless.
 */
export function qualityFilterLoss(items: readonly FileForProcessing[]): QualityFilterLoss | null {
  const counted = items.filter(
    (i): i is FileForProcessing & { pipelineInformation: IPipelineInformation } =>
      i.itemType !== 'NEGATIVE_CONTROL' &&
      !!i.pipelineInformation &&
      i.pipelineInformation.sequenceCountAfterQualityFiltered != null &&
      readsBefore(i.pipelineInformation) > 0
  )
  if (counted.length === 0) return null

  let before = 0
  let after = 0
  let severeSamples = 0
  for (const { pipelineInformation: pi } of counted) {
    const sampleBefore = readsBefore(pi)
    const sampleAfter = pi.sequenceCountAfterQualityFiltered ?? 0
    before += sampleBefore
    after += sampleAfter
    if (lostPercent(sampleBefore, sampleAfter) > QUALITY_FILTER_LOSS_WARN_PERCENT) severeSamples++
  }

  const orderLost = lostPercent(before, after)
  if (orderLost <= QUALITY_FILTER_LOSS_WARN_PERCENT && severeSamples * 2 <= counted.length) return null
  return {
    lostPercent: Math.round(orderLost * 10) / 10,
    severeSamples,
    samples: counted.length,
    lengthWindow: sharedLengthWindow(counted)
  }
}

const describeLoss = (loss: QualityFilterLoss): string =>
  `Quality filtering removed ${loss.lostPercent.toFixed(1)} % of reads; ${loss.severeSamples} of ${loss.samples} samples ` +
  `lost more than ${QUALITY_FILTER_LOSS_WARN_PERCENT} %. This usually means the read-length window` +
  (loss.lengthWindow ? ` (${loss.lengthWindow})` : '') +
  ' does not match the actual read lengths, or the wrong primer pair was set.'

/** The warning to show before postprocessing starts, null when the quality filter looks ordinary. */
export function qualityFilterLossWarning(items: readonly FileForProcessing[]): string | null {
  const loss = qualityFilterLoss(items)
  if (!loss) return null
  return (
    describeLoss(loss) +
    ' Postprocessing these reads gives a report built on a small, possibly unrepresentative remainder. ' +
    'Check both under Manage -> Change Data and resubmit before postprocessing.'
  )
}

/** The order-page alert for a severe quality-filter loss, empty otherwise. Computed on every view, for every order member. */
export function qualityFilterLossAlerts(items: readonly FileForProcessing[]): OrderAlert[] {
  const loss = qualityFilterLoss(items)
  if (!loss) return []
  return [
    {
      id: 'computed:quality-filter-loss',
      title: `Quality filtering removed ${loss.lostPercent.toFixed(1)} % of reads`,
      body: describeLoss(loss),
      audience: null,
      resolved: false
    }
  ]
}
